import { ChangeEvent, FC, useEffect, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { useGetEnumsQuery } from "../../../../app/globalApi";
import { OptionType } from "../../../../helpers/utils/StyleUtils";
import { handleRTKQuery } from "../../../../helpers/utils/RtkQueryUtils";
import Modal from "../../../../components/common/form/Modal";
import Form from "../../../../components/common/form/Form";
import InputField from "../../../../components/common/form/InputField";
import SelectField from "../../../../components/common/form/SelectField";
import { createOptionListForSelectTag } from "../../../../helpers/utils/Utils";
import Button from "../../../../components/common/form/Button";
import { useCreateReswareTypeMappingMutation, useGetReswareTypeMappingByIdQuery, useUpdateReswareTypeMappingMutation } from "../integrationsApi";

interface Props {
    show: boolean;
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
    customerId?: string;
    mappingId?: string;
    isReadOnly?: boolean;
}

const AddEditReswareTypeMappingsForm: FC<Props> = ({ show, setShow, customerId, mappingId }) => {
    const [formData, setFormData] = useState<any>({});
    const [createTypeMapping, { isLoading: isCreating }] = useCreateReswareTypeMappingMutation();
    const [updateTypeMapping, { isLoading: isUpdating }] = useUpdateReswareTypeMappingMutation();
    const { data: mappingData } = useGetReswareTypeMappingByIdQuery(
        customerId && mappingId ? { customerId, mappingId } : skipToken
    );
    const { data: enums } = useGetEnumsQuery("");

    useEffect(() => {
        if (mappingData) {

            setFormData({
                ...mappingData,
                mapFrom: mappingData.mapFrom
                    ? enums?.["ProductType"]
                        ?.filter((el: any) => el.constant === mappingData.mapFrom)
                        ?.map((el: any) => ({
                            value: mappingData.mapFrom,
                            label: el.value
                        }))[0] // assuming you want a single object, not an array
                    : null,
                type: mappingData.type
                    ? enums?.["ReswareType"]
                        ?.filter((el: any) => el.constant === mappingData.type)
                        ?.map((el: any) => ({
                            value: mappingData.type,
                            label: el.value
                        }))[0] // assuming you want a single object, not an array
                    : null
            });
        }
    }, [mappingData]);

    const handleInputChange = (e: ChangeEvent<HTMLInputElement> | { name: string; value: OptionType | null }) => {
        const { name, value } = "target" in e ? e.target : e;
        setFormData((prevData: any) => ({ ...prevData, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const credentialPayload = {
            ...formData,
            customerId: customerId,
            type: formData?.type?.value,
            mapFrom: formData?.mapFrom?.value
        };

        const apiCall = mappingId
            ? () => updateTypeMapping({ id: mappingId, data: credentialPayload }).unwrap()
            : () => createTypeMapping({ id: customerId, data: credentialPayload }).unwrap();

        await handleRTKQuery(apiCall, (isSuccess: boolean) => {
            if (isSuccess) {
                setFormData({});
                setShow(false);
            }
        });
    };

    return (
        <Modal
            title={mappingId ? "Update Resware Type Mapping" : "Add Resware Type Mapping"}
            show={show}
            setShow={setShow}
            className="w-2/5 bg-text-0 rounded-lg shadow-lg"
            fields={
                <Form onSubmit={handleSubmit} fields={
                    <div className="text-sm">
                        <SelectField
                            placeholder="Select Type..."
                            value={formData?.type || null}
                            options={createOptionListForSelectTag(enums?.["ReswareType"], "value", "constant")}
                            onChange={(selectedOption) => handleInputChange({ name: "type", value: selectedOption })}
                            label="Type"
                            required={true}
                        />

                        <SelectField
                            placeholder="Select map from..."
                            value={formData?.mapFrom || null}
                            options={createOptionListForSelectTag(enums?.["ProductType"], "value", "constant")}
                            onChange={(selectedOption) => handleInputChange({ name: "mapFrom", value: selectedOption })}
                            label="Map From"
                            required={true}
                        />

                        <InputField
                            label="Map To"
                            name="mapTo"
                            value={formData?.mapTo || ""}
                            type="text"
                            onChange={handleInputChange}
                            className="mb-2"
                            required
                        />

                        <InputField
                            label="Map To Reorder"
                            name="mapToReorder"
                            value={formData?.mapToReorder || ""}
                            type="text"
                            onChange={handleInputChange}
                            className="mb-2"
                            required
                        />

                        <div className="mt-4 border-t border-t-surface-5 flex justify-end pt-4 gap-3">
                            <Button btnText="Cancel" type="close" onClick={() => setShow(false)} />
                            <Button
                                btnText={mappingId ? "Update" : "Submit"}
                                isLoading={mappingId ? isUpdating : isCreating}
                                type="submit"
                            />
                        </div>
                    </div>
                } />
            }
        />
    );
};

export default AddEditReswareTypeMappingsForm;
